import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { SEO } from "@/components/common/SEO";
import { COMPANY } from "@/i18n/translations";
import { INDUSTRY_ICONS, INDUSTRY_DETAILS } from "@/data/static";
import { Building2 } from "lucide-react";
import { LeadForm } from "@/components/common/LeadForm";

export default function Industries() {
  const { t } = useLang();
  const industries = t("industries.items");

  return (
    <div data-testid="industries-page">
      <SEO
        title={`Industries Served — Logistics, Commercial, Residential | ${COMPANY.brand}`}
        description="Orion FM serves UAE logistics, commercial, residential, retail, healthcare and education facilities with integrated FM."
      />

      <section className="bg-white py-20 lg:py-28">
        <div className="container-orion">
          <div className="text-eyebrow">{t("industries.sectionLabel")}</div>
          <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium leading-[1.05] text-slate-900 max-w-4xl">
            {t("industries.sectionTitle")}
          </h1>
        </div>
      </section>

      <section className="pb-24 bg-white">
        <div className="container-orion grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {industries.map((it, i) => {
            const Icon = INDUSTRY_ICONS[it.slug] || Building2;
            const isPrimary = it.slug === "logistics";
            const isSpotlight = it.slug === "residential";
            const spotlightImage = isSpotlight ? INDUSTRY_DETAILS[it.slug]?.image : null;

            // Residential — rich photo-backed card
            if (isSpotlight) {
              return (
                <Link
                  key={it.slug}
                  to={`/industries/${it.slug}`}
                  data-testid={`industries-card-${it.slug}`}
                  className="relative rounded-2xl overflow-hidden card-hover group border border-slate-200 min-h-[320px] flex flex-col justify-end"
                >
                  <img
                    src={spotlightImage}
                    alt={it.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-slate-950/10" />
                  <div className="relative p-7 text-white">
                    <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-semibold text-blue-200 border border-blue-300/40 rounded-full px-2.5 py-1 bg-blue-500/10 backdrop-blur-sm">
                      <Icon className="w-3 h-3" />
                      Community FM
                    </span>
                    <h2 className="mt-4 font-heading text-2xl font-medium leading-tight">
                      {it.title}
                    </h2>
                    <p className="mt-2 text-sm text-slate-300 leading-relaxed">
                      {it.desc}
                    </p>
                    <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-200">
                      Explore residential FM
                      <span className="text-blue-300">→</span>
                    </div>
                  </div>
                </Link>
              );
            }

            const content = (
              <>
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    isPrimary ? "bg-blue-600 text-white" : "bg-blue-50 text-blue-700"
                  }`}
                >
                  <Icon className="w-6 h-6" />
                </div>
                {isPrimary && (
                  <span className="absolute top-7 right-7 text-[10px] uppercase tracking-[0.18em] font-semibold text-blue-300 border border-blue-400/40 rounded-full px-2.5 py-1">
                    Primary Focus
                  </span>
                )}
                <h2
                  className={`mt-6 font-heading font-medium ${
                    isPrimary ? "text-2xl lg:text-3xl text-white" : "text-xl text-slate-900"
                  }`}
                >
                  {it.title}
                </h2>
                <p
                  className={`mt-3 leading-relaxed ${
                    isPrimary ? "text-slate-400 text-base max-w-xl" : "text-sm text-slate-600"
                  }`}
                >
                  {it.desc}
                </p>
                {isPrimary && (
                  <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-300">
                    Explore warehouse FM
                    <span className="text-blue-400">→</span>
                  </div>
                )}
              </>
            );
            const className = `relative rounded-2xl p-8 card-hover ${
              isPrimary
                ? "bg-slate-950 text-white border border-slate-800 lg:col-span-2 lg:row-span-1 hover:border-blue-500/50"
                : "bg-white border border-slate-200"
            }`;
            return isPrimary ? (
              <Link
                key={it.slug}
                to="/industries/logistics"
                data-testid={`industries-card-${it.slug}`}
                className={className}
              >
                {content}
              </Link>
            ) : (
              <Link
                key={it.slug}
                to={`/industries/${it.slug}`}
                data-testid={`industries-card-${it.slug}`}
                className={className}
              >
                {content}
              </Link>
            );
          })}
        </div>
      </section>

      <section className="py-24 lg:py-28 bg-slate-50">
        <div className="container-orion grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6">
            <div className="text-eyebrow text-blue-700">{t("leadForm.heading")}</div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium text-slate-900 leading-tight">
              {t("contact.title")}
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">{t("leadForm.sub")}</p>
          </div>
          <div className="lg:col-span-6">
            <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-xl shadow-blue-900/5">
              <LeadForm pageSource="industries" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
